import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BaseControlDirective} from '../../directives/base-control.directive';
import {distinctUntilChanged, debounceTime } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => InputComponent), multi: true},
  ],
})
export class InputComponent extends BaseControlDirective implements ControlValueAccessor, AfterViewInit, OnChanges {
  @Input() icon = '';
  @Input() hidePassword = true;
  @Input() type: 'text' | 'email' | 'password' | 'number' | 'addedNdcQty' | 'phone' | 'textUpperCase' = 'text';
  @Input() label = '';
  @Input() prefix = '';
  @Input() paddingLeft = '12px';
  @Input() inputMask = '';
  @Input() optional: boolean;
  @Input() autofocus: boolean;
  @Input() autoselect: boolean;
  @Input() naturalNumbersOnly: boolean;
  @Input() datepicker: boolean;
  @Input() datepickerMask = '00/00/0000';
  @Input() textAlign: 'left' | 'right' | 'center' = 'left';
  @Input() phoneInputMask = '(000) 000-0000';
  @Input() maxLength = 524288; // default for text input
  @Input() isZipCodeNumber: boolean;
  @Input() isNdcQty: boolean;
  @Input() showFocusedErrorBorder: boolean;
  @Input() set initialValue(value: string) {
    this.value = value;
  }
  @Input() ignoreInput: boolean;
  @Input() readOnly: boolean;

  @Input() showErrors = true;
  @Input() errorMessage = '';
  @Input() invalid: boolean;
  @Input() customClasses = ''; // any classes, e.g. tailwind that you want to apply to <input>
  @Input() iconClasses = '';
  @Input() symbolsLeftCounter: boolean;
  @Input() allowedSymbols: string[];

  autocomplete: 'off' | 'on' = 'on';
  @Input() set disableAutocomplete (value: boolean) {
    if(value) {
      this.autocomplete = 'off';
    }
  }

  @Input() errorRequiredMessage: string = 'This field is required';
  @Input() id: string;

  @Input() tooltipText: string;
  @Input() tooltipIconName: string = 'icon-circle-alert-blue';
  @Input() tooltipTextWidth: number;
  @Input() isErrorNoWrap: boolean = false;
  @Input() hideErrorIcon: boolean;
  @Input() errorsDescription: any;

  @Output() keyupEnter: EventEmitter<Event> = new EventEmitter();
  @Output() keydownTab: EventEmitter<Event> = new EventEmitter();
  @Output() keyupBackspace: EventEmitter<Event> = new EventEmitter();
  @Output() blurred: EventEmitter<Event> = new EventEmitter();
  @Output() focused: EventEmitter<Event> = new EventEmitter();
  @Output() valueChanged: EventEmitter<string> = new EventEmitter();
  @Output() onIconClick: EventEmitter<void> = new EventEmitter();

  @ViewChild('appInput') appInput: ElementRef;

  @HostListener('paste', ['$event'])
  onCtrlV(ev): void {
    const pastedText = ev.clipboardData.getData('text') as string;

    if (this.allowedSymbols?.length && pastedText.split('').some(x => !this.allowedSymbols.includes(x))) {
      //not allowed symbol pasted
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    if (this.isZipCodeNumber) {
      ev.preventDefault();
      ev.stopPropagation();

      this.control.setValue(pastedText.replace(/\D/g, '').slice(0, 5));
      this.control.updateValueAndValidity();
    }

    if (
      (this.type === 'number' && this.naturalNumbersOnly && +pastedText % 1 !== 0) ||
      this.hasWrongEmptySpaces(pastedText)
    ) {
      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  ngAfterViewInit(): void {
    if (this.autofocus) {
      setTimeout(() => this.appInput.nativeElement.focus());
    }

    if (this.autoselect) {
      setTimeout(() => this.appInput.nativeElement.select());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.appInput && changes.autofocus &&
      changes.autofocus.currentValue !== changes.autofocus.previousValue &&
      changes.autofocus.currentValue === true
    ) {
      this.selectInput();
    }

    if (
      this.appInput && changes.autoselect &&
      changes.autoselect.currentValue !== changes.autoselect.previousValue &&
      changes.autoselect.currentValue === true
    ) {
      this.focusInput();
    }
  }

  focusInput(): void {
    setTimeout(() => this.appInput.nativeElement.focus());
  }

  selectInput(): void {
    setTimeout(() => this.appInput.nativeElement.select());
  }

  onBlur(): void {
    super.onBlur();

    if (this.value && (!this.type || this.type.includes('text'))) {
      this.value = this.value.trim();
    }

    this.blurred.next();
  }

  onFocus(): void {
    super.onFocus();
    this.focused.next();
  }

  hasWrongEmptySpaces(value: string = '', increment: string = ''): boolean {
    const current = value + increment;
    const emptySpacesOnly = current.length > 1 && !current.trim().length;
    const hasEmptySpacesInside = current.includes('  ');

    return current === ' ' || emptySpacesOnly || hasEmptySpacesInside;
  }

  willCreateWSS(ev): boolean {
    const elmInput = ev.currentTarget;
    const content = elmInput.value;

    const posStart = elmInput.selectionStart;
    const posEnd = elmInput.selectionEnd;

    return ev.key === ' ' && (!content[posStart - 1] || content[posStart - 1] === ' ' || content[posEnd] === ' ');
  }

  onKeyDown(ev: KeyboardEvent): void {
    if(this.ignoreInput && ev.key !== 'Backspace' && ev.key !== 'Delete')
      ev.preventDefault();

    if (this.control) {
      this.control.markAsTouched();
      this.control.updateValueAndValidity();
    }
    //Ignore input + allowed symbols will let you choose what user can input
    if(this.allowedSymbols?.length && this.allowedSymbols?.includes(ev.key)) {
      ev.preventDefault();
      ev.stopPropagation();
      const val = this.control.value;
      this.control.setValue(val + ev.key);
     return;
    }
    
    if (this.type === 'addedNdcQty') {

      // this.control.setValue(ev.replace(/\D/g, "").slice(0, 5));
      if (ev.key !== 'Backspace' && ev.key !== 'Delete') {
        ev.preventDefault();
      }

      if (ev.key.match('^[0-9]*$')) {
        const oldval = Math.abs(this.control.value as number);
        let newVal = '';
        if (isNaN(oldval)) {
          newVal = ev.key;
        } else {
          newVal = oldval.toString() + ev.key;
        }
        const newNum = parseInt(newVal, 10);
        this.control.setValue(0 - newNum);
      }
    } 
    if (
      this.datepicker || this.willCreateWSS(ev) ||
      (this.hasWrongEmptySpaces(this.value, ev.key) && ev.code !== 'Backspace')
    ) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    if (this.naturalNumbersOnly && ev.code === 'NumpadDecimal') {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    if (!this.value || this.type !== 'number') {
      return;
    }

    const allow: boolean = !!ev?.code?.match(/Arrow|Delete|Backspace|Tab|MetaKey|KeyV|Numpad/);
    const isNumberKey: boolean = !!ev?.key?.match(/[0-9]/);

    if (allow || (isNumberKey && (this.maxLength > this.value.toString().length))) {
      return;
    }

    ev.preventDefault();
    ev.stopPropagation();
  }

  toggleHidePassword(): void {
    this.hidePassword = !this.hidePassword;
  }

  getErrorMessage(): string {
    const errors = this.control.errors;
    if (errors) {
      if (errors.required) {
        return this.errorRequiredMessage;
      } else if (errors.emailTaken) {
        return 'Email is already in use';
      } else if (errors.minlength) {
        return 'Minimum ' + (errors.minlength.requiredLength) + ' symbols';
      } else if (errors.maxlength) {
        return 'Maximum ' + (errors.maxlength.requiredLength) + ' symbols';
      } else if (errors.min) {
        return  this.errorsDescription?.min || 'Must be greater than ' + (errors.min.min - 1);
      } else if (errors.max) {
        return this.errorsDescription?.max || 'Must be less than ' + (errors.max.max + 1);
      } else if (errors.email) {
        return 'Please enter valid email';
      } else if (errors.password === 'invalid') {
        return 'This password is invalid, please try again';
      } else if (errors.password === 'same') {
        return 'New password should be different';
      } else if (errors.password === 'weak') {
        return 'Password should be at least 8 characters long';
      } else if (errors.password === 'mediate') {
        return 'Password requires at least one number';
      } else if (errors.password === 'strong') {
        return 'Please provide lowercase and uppercase characters';
      } else if (errors.invalidNPI) {
        return 'NPI Number not valid.';
      } else if (errors.npiInUse) {
        return 'NPI Number already in use.';
      } else if (errors.cognitoIssue) {
        return 'The provided email is not valid.';
      } else if (errors.duplicateNdc) {
        return 'NDC already in use.';
      }
    }

    return '';
  }
}
